/**
 * User Badge Section Component
 * Displays a user's current badges and provides badge management functionality
 * Shows in the user edit modal for admin badge assignment/revocation
 */

import { useState } from 'react';
import {
  useUserBadges,
  useAdminBadgesList,
  useRevokeBadge,
  useSetUserBadgeHidden,
} from '../../hooks/useBadgeApi';
import { useAdminUserEntitlements } from '../../hooks/useAdminApi';
import { ApiHttpError } from '../../lib/authenticated-api';
import { entitlementOfGrant } from '../../lib/types/badge';
import { type UserProfile } from '../../lib/types/user';
import {
  BadgeAssignmentModal,
  type AutomaticGrant,
} from './BadgeAssignmentModal';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { AnimatedMessage } from '../ui/AnimatedMessage';
import { BadgeIcon } from '../badges/BadgeIcon';

interface UserBadgeSectionProps {
  user: UserProfile;
}

interface BadgeCardProps {
  badgeId: string;
  badgeName: string;
  badgeIconUrl: string;
  // Flattened rather than passed as a definition, matching the props either
  // side of it. Without this the card cannot tell a badge with no icon by
  // design from one still awaiting artwork, because both arrive as ''.
  badgeImageless?: boolean;
  earnedAt: string;
  // The entitlement an automatic grant was made for (#2061); null for a
  // manual grant.
  automaticFor: string | null;
  // The badge is automatic-only (#2268): held only while the user holds its
  // linked entitlement.
  automaticOnly: boolean;
  hidden: boolean;
  onRevoke: (badgeId: string) => void;
  onSetHidden: (badgeId: string, hidden: boolean) => void;
  isRevoking: boolean;
  isChangingVisibility: boolean;
}

function BadgeCard({
  badgeId,
  badgeName,
  badgeIconUrl,
  badgeImageless,
  earnedAt,
  automaticFor,
  automaticOnly,
  hidden,
  onRevoke,
  onSetHidden,
  isRevoking,
  isChangingVisibility,
}: BadgeCardProps) {
  const earnedDate = new Date(earnedAt);
  const timeAgo = formatTimeAgo(earnedDate);

  return (
    <div
      data-testid={`user-badge-${badgeId}`}
      className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
    >
      <div className="flex items-center space-x-3">
        {/* Shared BadgeIcon rather than a local onError fallback, which here
            pointed at a third-party dicebear URL carrying the badge id. */}
        <BadgeIcon
          src={badgeIconUrl}
          alt={badgeName}
          size="sm"
          className={hidden ? 'rounded-full opacity-50' : 'rounded-full'}
          imageless={badgeImageless}
        />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {badgeName}
            {hidden && (
              <span
                data-testid={`user-badge-hidden-${badgeId}`}
                className="ml-2 inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-200"
                title="Hidden by an admin: not shown on the user's profile or in any product"
              >
                Hidden
              </span>
            )}
            {automaticOnly && (
              <span
                data-testid={`user-badge-automatic-only-${badgeId}`}
                className="ml-2 inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200"
                title="Automatic only: held only while the user holds the linked entitlement, and never granted by hand"
              >
                Automatic only
              </span>
            )}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            <span data-testid={`user-badge-origin-${badgeId}`}>
              {automaticFor ? `Automatic — ${automaticFor}` : 'Manual'}
            </span>
            {' · '}Earned {timeAgo}
          </p>
        </div>
      </div>
      <div className="flex items-center space-x-3">
        <button
          type="button"
          data-testid={`user-badge-${hidden ? 'unhide' : 'hide'}-${badgeId}`}
          onClick={() => onSetHidden(badgeId, !hidden)}
          disabled={isChangingVisibility}
          className="text-sm font-medium text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {hidden ? 'Unhide' : 'Hide'}
        </button>
        <button
          type="button"
          data-testid={`user-badge-revoke-${badgeId}`}
          onClick={() => onRevoke(badgeId)}
          disabled={isRevoking}
          className="text-red-600 hover:text-red-500 dark:text-red-400 dark:hover:text-red-300 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          title="Revoke badge"
          aria-label={`Revoke ${badgeName}`}
        >
          {isRevoking ? (
            <LoadingSpinner className="w-4 h-4" />
          ) : (
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          )}
        </button>
      </div>
    </div>
  );
}

// Helper function to format time ago
function formatTimeAgo(date: Date): string {
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) return 'just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 2592000)
    return `${Math.floor(diffInSeconds / 86400)}d ago`;

  return date.toLocaleDateString();
}

export function UserBadgeSection({ user }: UserBadgeSectionProps) {
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [revokeConfirm, setRevokeConfirm] = useState<{
    badgeId: string;
    badgeName: string;
    reason: string;
    // The server's 409 (#2061): an automatic grant whose entitlement is still
    // active. Shown in the dialog, which then offers hiding instead.
    conflict: string | null;
    // A manual grant of a badge linked to an entitlement the user holds:
    // removing it is allowed, but the badge comes straight back as automatic.
    returnsAsAutomaticFor: string | null;
  } | null>(null);
  const [visibilityConfirm, setVisibilityConfirm] = useState<{
    badgeId: string;
    badgeName: string;
    hidden: boolean;
    reason: string;
  } | null>(null);

  const {
    data: userBadgesData,
    isLoading: isLoadingUserBadges,
    error: userBadgesError,
  } = useUserBadges(user.uid);

  const { isLoading: isLoadingAllBadges } = useAdminBadgesList({
    limit: 100,
  });

  // Shares its cache entry with the card's Access panel. Used only to warn
  // before a manual grant is revoked; the server makes every refusal.
  const { data: userEntitlements } = useAdminUserEntitlements(user.uid);

  const revokeBadge = useRevokeBadge();
  const setBadgeHidden = useSetUserBadgeHidden();

  const handleRevokeBadge = (badgeId: string) => {
    const userBadge = userBadgesData?.badges.find(b => b.badgeId === badgeId);
    if (!userBadge?.badge) return;

    const linked = userBadge.badge.linked_entitlement;
    const isManual = entitlementOfGrant(userBadge.granted_by) === null;
    const linkedIsActive =
      !!linked && userEntitlements?.[linked]?.isActive === true;
    // An automatic-only badge (#2268) is not given back: the server refuses
    // the revoke (409) while the entitlement is active, and the dialog says
    // so when it does.
    const automaticOnly = userBadge.badge.automatic_only === true;

    setRevokeConfirm({
      badgeId,
      badgeName: userBadge.badge.name,
      reason: '',
      conflict: null,
      returnsAsAutomaticFor:
        isManual && linkedIsActive && !automaticOnly ? linked : null,
    });
  };

  const confirmRevoke = async () => {
    if (!revokeConfirm) return;

    try {
      await revokeBadge.mutateAsync({
        userId: user.uid,
        badgeId: revokeConfirm.badgeId,
        reason: revokeConfirm.reason || undefined,
      });
      setRevokeConfirm(null);
    } catch (error) {
      // A 409 is the server refusing to remove an automatic grant while its
      // entitlement is active: keep the dialog open and explain. Every other
      // failure is reported by the mutation's toast.
      if (error instanceof ApiHttpError && error.status === 409) {
        setRevokeConfirm({ ...revokeConfirm, conflict: error.serverMessage });
      }
    }
  };

  const handleSetHidden = (badgeId: string, hidden: boolean) => {
    const userBadge = userBadgesData?.badges.find(b => b.badgeId === badgeId);
    if (!userBadge?.badge) return;
    setVisibilityConfirm({
      badgeId,
      badgeName: userBadge.badge.name,
      hidden,
      reason: '',
    });
  };

  const confirmVisibility = async () => {
    if (!visibilityConfirm) return;

    try {
      await setBadgeHidden.mutateAsync({
        userId: user.uid,
        badgeId: visibilityConfirm.badgeId,
        hidden: visibilityConfirm.hidden,
        reason: visibilityConfirm.reason || undefined,
      });
      setVisibilityConfirm(null);
    } catch (_error) {
      // Error handled by mutation
    }
  };

  if (isLoadingUserBadges) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
            Badges
          </h4>
        </div>
        <div className="flex items-center justify-center h-32">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    );
  }

  if (userBadgesError) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
            Badges
          </h4>
        </div>
        <AnimatedMessage type="error" message="Failed to load user badges" />
      </div>
    );
  }

  const currentBadges = userBadgesData?.badges || [];
  const hiddenCount = currentBadges.filter(b => b.hidden === true).length;
  // Badges held by an automatic grant: the assignment modal offers them for
  // conversion to a manual grant (#2061).
  const automaticGrants: Record<string, AutomaticGrant> = {};
  for (const b of currentBadges) {
    const entitlement = entitlementOfGrant(b.granted_by);
    if (entitlement) {
      automaticGrants[b.badgeId] = { entitlement, hidden: b.hidden === true };
    }
  }

  return (
    <div className="space-y-4" data-testid="user-badge-section">
      {/* Section Header */}
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
            Badges
          </h4>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {currentBadges.length} badge{currentBadges.length !== 1 ? 's' : ''}{' '}
            earned
            {hiddenCount > 0 && `, ${hiddenCount} hidden`}
          </p>
        </div>
        <button
          onClick={() => setShowAssignModal(true)}
          disabled={isLoadingAllBadges}
          className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
              clipRule="evenodd"
            />
          </svg>
          Manage Badges
        </button>
      </div>

      {/* Current Badges */}
      <div className="space-y-3">
        {currentBadges.length === 0 ? (
          <div className="text-center py-8">
            <svg
              className="w-12 h-12 mx-auto text-gray-400 dark:text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              No badges earned yet
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500">
              Click &ldquo;Manage Badges&rdquo; to grant badges to this user
            </p>
          </div>
        ) : (
          currentBadges.map(userBadge => {
            // The API response includes full badge details in the 'badge' property
            const badgeDefinition = userBadge.badge;

            if (!badgeDefinition) return null;

            // earned_at is already an ISO string from the API
            const earnedDate = userBadge.earned_at;

            return (
              <BadgeCard
                key={userBadge.badgeId}
                badgeId={userBadge.badgeId}
                badgeName={badgeDefinition.name}
                badgeIconUrl={badgeDefinition.icon_url}
                badgeImageless={badgeDefinition.imageless}
                earnedAt={earnedDate}
                automaticFor={entitlementOfGrant(userBadge.granted_by)}
                automaticOnly={badgeDefinition.automatic_only === true}
                hidden={userBadge.hidden === true}
                onRevoke={handleRevokeBadge}
                onSetHidden={handleSetHidden}
                isRevoking={
                  revokeBadge.isPending &&
                  revokeBadge.variables?.badgeId === userBadge.badgeId
                }
                isChangingVisibility={
                  setBadgeHidden.isPending &&
                  setBadgeHidden.variables?.badgeId === userBadge.badgeId
                }
              />
            );
          })
        )}
      </div>

      {/* Badge Assignment Modal */}
      {showAssignModal && (
        <BadgeAssignmentModal
          user={user}
          currentBadges={currentBadges.map(b => b.badgeId)}
          automaticGrants={automaticGrants}
          isOpen={showAssignModal}
          onClose={() => setShowAssignModal(false)}
        />
      )}

      {/* Revoke Confirmation Dialog */}
      {revokeConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div
            role="dialog"
            aria-label="Revoke Badge"
            className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4"
          >
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
              Revoke Badge
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Are you sure you want to revoke the &ldquo;
              {revokeConfirm.badgeName}&rdquo; badge from {user.display_name}?
            </p>

            {revokeConfirm.returnsAsAutomaticFor && (
              <p
                data-testid="revoke-returns-warning"
                className="mb-4 rounded-md bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-900/30 dark:text-amber-200"
              >
                This badge is granted automatically by{' '}
                <code>{revokeConfirm.returnsAsAutomaticFor}</code>, which this
                user holds. Revoking this hand-made grant gives it straight back
                as an automatic one. To take it off their profile, hide it
                instead.
              </p>
            )}

            {revokeConfirm.conflict ? (
              <div
                data-testid="revoke-conflict"
                className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-800 dark:bg-red-900/30 dark:text-red-200"
              >
                <p className="font-medium">This badge cannot be revoked.</p>
                <p className="mt-1">
                  It is granted automatically by an entitlement the user still
                  holds, so it would only be granted again. Remove the
                  entitlement or hide the badge.
                </p>
                <p className="mt-1 text-xs">{revokeConfirm.conflict}</p>
              </div>
            ) : (
              <div className="mb-4">
                <label
                  htmlFor="revoke-reason"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  Reason (optional)
                </label>
                <textarea
                  id="revoke-reason"
                  value={revokeConfirm.reason}
                  onChange={e =>
                    setRevokeConfirm({
                      ...revokeConfirm,
                      reason: e.target.value,
                    })
                  }
                  rows={3}
                  placeholder="Explain why this badge is being revoked..."
                  className="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
                />
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setRevokeConfirm(null)}
                disabled={revokeBadge.isPending}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
              >
                {revokeConfirm.conflict ? 'Close' : 'Cancel'}
              </button>
              {revokeConfirm.conflict ? (
                <button
                  data-testid="revoke-conflict-hide"
                  onClick={() => {
                    const { badgeId, badgeName } = revokeConfirm;
                    setRevokeConfirm(null);
                    setVisibilityConfirm({
                      badgeId,
                      badgeName,
                      hidden: true,
                      reason: '',
                    });
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-sky-600 hover:bg-sky-700 rounded-md"
                >
                  Hide instead
                </button>
              ) : (
                <button
                  data-testid="revoke-confirm"
                  onClick={confirmRevoke}
                  disabled={revokeBadge.isPending}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md disabled:opacity-50"
                >
                  {revokeBadge.isPending ? 'Revoking...' : 'Revoke Badge'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Hide / Unhide Confirmation Dialog (#2061) */}
      {visibilityConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div
            role="dialog"
            aria-label={
              visibilityConfirm.hidden ? 'Hide Badge' : 'Unhide Badge'
            }
            className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4"
          >
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
              {visibilityConfirm.hidden ? 'Hide Badge' : 'Unhide Badge'}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {visibilityConfirm.hidden ? (
                <>
                  &ldquo;{visibilityConfirm.badgeName}&rdquo; stays assigned to{' '}
                  {user.display_name} but is no longer shown on their profile or
                  in any product. Only admins see it, marked hidden. If it is
                  automatic and the entitlement is removed, the badge goes with
                  it, and comes back visible if the entitlement is granted
                  again.
                </>
              ) : (
                <>
                  &ldquo;{visibilityConfirm.badgeName}&rdquo; is shown on{' '}
                  {user.display_name}&rsquo;s profile and in products again.
                </>
              )}
            </p>

            <div className="mb-4">
              <label
                htmlFor="visibility-reason"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Reason (optional)
              </label>
              <textarea
                id="visibility-reason"
                value={visibilityConfirm.reason}
                onChange={e =>
                  setVisibilityConfirm({
                    ...visibilityConfirm,
                    reason: e.target.value,
                  })
                }
                rows={2}
                maxLength={500}
                className="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setVisibilityConfirm(null)}
                disabled={setBadgeHidden.isPending}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                data-testid="visibility-confirm"
                onClick={confirmVisibility}
                disabled={setBadgeHidden.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-sky-600 hover:bg-sky-700 rounded-md disabled:opacity-50"
              >
                {setBadgeHidden.isPending
                  ? 'Saving...'
                  : visibilityConfirm.hidden
                    ? 'Hide Badge'
                    : 'Unhide Badge'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
